import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Check, Loader2 } from "lucide-react";
import { getSaveStatus, retryNow, subscribeSaveStatus } from "../../util/saveTracker";

/**
 * The "Saved" pill (docs/design/HANDOFF.md, "Saved toast"; MainSaved,
 * Tab1TitleSaved, Desk1TitleSaved): centred just below the header, floating
 * over the page. Driven by util/saveTracker.js.
 *
 * "Try now" and the "No connection…" wording after a minute are not drawn
 * (docs/design/GAPS.md, decided 2026-10-23).
 *
 * Mounted in the header but drawn at the page level (a portal), just below
 * where the header ends, so it shows above sheets and pop-ups (the dedication
 * sheet saves too) instead of under their overlay.
 */
const shell = "pointer-events-auto flex items-center gap-2 rounded-[22px] py-[9px] pl-2.5 pr-4 text-[15px] font-bold shadow-[0_6px_18px_rgba(27,13,18,0.14)]";

const SavePill = () => {
  const [save, setSave] = useState(getSaveStatus);
  useEffect(() => subscribeSaveStatus(setSave), []);
  const anchorRef = useRef(null);
  const [top, setTop] = useState(64);
  const visible = save.status !== "idle";
  useLayoutEffect(() => {
    if (!visible) return undefined;
    const header = anchorRef.current?.closest("header");
    const place = () => setTop(Math.max(0, (header?.getBoundingClientRect().bottom ?? 54)) + 10);
    place();
    window.addEventListener("scroll", place, { passive: true });
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place);
      window.removeEventListener("resize", place);
    };
  }, [visible]);

  let pill = null;
  if (save.status === "saved") {
    pill = (
      <div className={`${shell} border border-green-200 bg-white text-green-800 transition-opacity duration-300 ${save.fading ? "opacity-0" : "opacity-100"}`}>
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100">
          <Check className="h-4 w-4" strokeWidth={3} aria-hidden="true" />
        </span>
        Saved
      </div>
    );
  } else if (save.status === "saving") {
    pill = (
      <div className={`${shell} border border-md-pink-light-3 bg-white pl-3 text-[#4A4446]`}>
        <Loader2 className="h-[18px] w-[18px] animate-spin text-md-pink-dark" aria-hidden="true" />
        Saving…
      </div>
    );
  } else if (save.status === "failed" || save.status === "offline") {
    pill = (
      <div role="alert" className={`${shell} max-w-[calc(100vw-32px)] border border-amber-300 bg-amber-50 pl-3 text-amber-950 md:max-w-[520px]`}>
        <AlertTriangle className="h-[18px] w-[18px] flex-shrink-0 text-amber-700" aria-hidden="true" />
        <span className="min-w-0 leading-snug">
          {save.status === "offline"
            ? "No connection. Your changes are kept on this device and will save when you're back online."
            : "Not saved yet. Trying again…"}
        </span>
        {/* TODO(design): "Try now" isn't drawn (docs/design/GAPS.md). */}
        <button
          type="button"
          onClick={retryNow}
          className="-my-2 ml-1 min-h-[44px] flex-shrink-0 rounded-full px-2 text-[15px] font-bold text-md-pink-dark underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-md-pink"
        >
          Try now
        </button>
      </div>
    );
  }

  return (
    <span ref={anchorRef} className="hidden">
      {createPortal(
        <div role="status" aria-live="polite" style={{ top }} className="pointer-events-none fixed inset-x-0 z-[90] flex justify-center px-4">
          {pill}
        </div>,
        document.body
      )}
    </span>
  );
};

export default SavePill;
